import * as R from 'ramda'
import * as AssessmentResultDto from '../assessmentResults/dto'
import {
  FlexibleScope,
  ClientFrontlineConfig,
  Criticality,
  Offer,
  OfferingQuestions,
  OfferingInteractionQuestion,
  OfferingQuestion,
  GroupOfferingQuestions,
  EquipmentGroupStId,
  DopAndAccessData
} from '../../../../types/types'
import {
  Offering,
  QuickTender,
  QuickTenderScopePending,
  FlexibleModuleScopes,
  Flexible,
  FlexibleRatingsAndSelections,
  FlexibleCriticalities,
  ValueAddedServicesAddendum,
  noRatings,
  fromDomain,
  ElevatorsEscalatorsApfs,
  Doors,
  DoorGroups,
  ElevatorEscalatorApfGroups,
  Interaction,
  ResiFlowInteraction,
  GSMCampaign // TODO: Remove as part of GSM Clean up
} from './types'
import { getInitialAnswersForInteraction, getInitialAnswersForAllModules } from './selections'
import {
  AssessmentResult,
  ValidForFlexible,
  AssessmentCriticality,
  ValidForValueAddedServiceAddendum,
  ElevatorAndEscalatorCriticality,
  AssessmentGroup,
  ValidForFlexibleDoors,
  OnlyDoorsAssessmentGroup,
  ValidForQuickTender,
  ValidForGSMCampaign // TODO: Remove as part of GSM Clean up
} from '../assessmentResults/types'
import {
  fillPricesForExtraServices,
  extractServices,
  fillPricesForExtraCommercialTerms,
  extractCommercialTerms
} from '../pricing/extra-services'
import { collectGroups } from '../assessmentResults/equipment'
import {
  getGroupOfferingQuestionsForQuickTenderScope,
  getInteractionOfferingQuestionsForQuickTenderScope
} from './questions'

export const enableEscalationOffer =
  (key: string, value: any) =>
  (offer: Offer): Offer => {
    const isEscalationFlow = { ...offer.isEscalationFlow, [key]: value }
    return {
      ...offer,
      isEscalationFlow
    }
  }

export const resetEscalationOffer =
  () =>
  (offer: Offer): Offer => {
    const isEscalationFlow = {
      ...offer.isEscalationFlow,
      active: false,
      rushContractType: '',
      initialContractAnnualPrice: 0,
      escalatedAnnualPrice: 0,
      interimTotalPrice: 0,
      numberOfEquipments: 0,
      escalatedPercentage: 0,
      finalizedTotalPrice: 0
    }
    return {
      ...offer,
      isEscalationFlow
    }
  }

export function updateRecommendedOffer(
  offer: Offer,
  frontlineConfig: ClientFrontlineConfig,
  checkAllCriticalitiesRetained?: (o: Offer) => boolean
): Offer {
  if (
    offer.interactionRating &&
    typeof checkAllCriticalitiesRetained === 'function' &&
    checkAllCriticalitiesRetained(offer)
  ) {
    return offer
  }
  const assessment = AssessmentResultDto.toDomain(offer, frontlineConfig)
  const recommendedOffering = updateRecommendedOffering(frontlineConfig, assessment, offer)
  const groups = collectGroups(assessment)
  const { equipmentGroups } = offer
  return {
    ...fromDomain(offer, recommendedOffering),
    extraServices:
      offer.extraServices && offer.extraServices.length > 0
        ? fillPricesForExtraServices(extractServices(recommendedOffering, groups, frontlineConfig), offer.extraServices)
        : extractServices(recommendedOffering, groups, frontlineConfig),
    extraCommercialTerms:
      offer.extraCommercialTerms && offer.extraCommercialTerms.length > 0
        ? fillPricesForExtraCommercialTerms(
            extractCommercialTerms(recommendedOffering, offer.commercialTerms, groups),
            offer.extraCommercialTerms
          )
        : extractCommercialTerms(recommendedOffering, offer.commercialTerms, groups),
    bundleGroupSelection: {},
    dopAndAccessData: {} as DopAndAccessData,
    prices: resetVasDiscount(offer),
    equipmentGroups
  }
}

const resetVasDiscount = (offer: Offer) => {
  const { prices } = offer
  return prices.map(price => ({
    ...price,
    vasServicesDiscountDetails: {}
  }))
}

// Given the current assessment result and the current Offering,
// gives back a new Offering.
export function updateRecommendedOffering(
  frontlineConfig: ClientFrontlineConfig,
  assessment: AssessmentResult,
  offer: Offer
): Offering {
  const { offeringQuestions: questions, enableDXOffering, enableCfalOffering } = frontlineConfig
  switch (assessment._tag) {
    case 'Invalid':
      // Assessment invalid, clear out all the ratings
      return noRatings
    case 'ValidForQuickTender':
      const konexionScope = R.pathOr([], ['quickTender', 'scopes'], frontlineConfig).filter(s => s === 'konexion')
      return konexionScope.length
        ? makeQuickTenderOffering(
            questions,
            konexionScope[0],
            assessment,
            offer,
            enableDXOffering.active,
            enableCfalOffering.active
          )
        : new QuickTenderScopePending({})
    case 'ValidForFlexible':
      return makeFlexibleOffering(
        questions.group,
        questions.interaction ? questions.interaction.flexible : [],
        assessment,
        offer,
        enableDXOffering.active,
        enableCfalOffering.active
      )
    case 'ValidForFlexibleDoors':
      return makeFlexibleDoorOffering(
        questions.group,
        questions.interaction ? questions.interaction.flexible : [],
        assessment,
        offer,
        enableDXOffering.active,
        enableCfalOffering.active
      )
    case 'ValidForValueAddedServiceAddendum':
      return makeAddendumServiceOffering(
        questions.group,
        questions.interaction ? questions.interaction.valueAddedServices : [],
        assessment,
        offer,
        enableDXOffering.active,
        enableCfalOffering.active
      )
    case 'ValidForGSMCampaign': // TODO: Remove as part of GSM Clean up
      return makeGSMServiceOffering(
        questions.group,
        [],
        assessment,
        offer,
        enableDXOffering.active,
        enableCfalOffering.active
      )
  }
}

function makeQuickTenderOffering(
  questions: OfferingQuestions,
  scope: 'konexion',
  assessment: ValidForQuickTender,
  offer: Offer,
  isDXEnabled: boolean,
  isCfalEnabled: boolean
) {
  const groupQuestions = getGroupOfferingQuestionsForQuickTenderScope(questions.group, scope)
  const interactionQuestions = getInteractionOfferingQuestionsForQuickTenderScope(questions.interaction!, scope)
  return new QuickTender(
    R.map(
      (g: any) => ({
        salesToolId: g.groupId,
        name: g.name,
        groupKind: g.groupKind,
        selections: getInitialAnswersForAllModules(
          groupQuestions,
          scope,
          offer,
          g.groupId,
          null,
          isDXEnabled,
          isCfalEnabled
        )
      }),
      assessment.groups
    ),
    scope,
    getInitialAnswersForInteraction(interactionQuestions, scope, offer)
  )
}

function makeFlexibleDoorOffering(
  questions: GroupOfferingQuestions,
  interactionQuestions: OfferingInteractionQuestion[],
  assessment: ValidForFlexibleDoors,
  offer: Offer,
  isDXEnabled: boolean,
  isCfalEnabled: boolean
): Flexible {
  const buildRatings = (
    groupQuestions: OfferingQuestion[],
    scope: FlexibleScope,
    groupId: EquipmentGroupStId,
    criticality: Criticality
  ): FlexibleRatingsAndSelections => ({
    ratings: moduleScopesForRating(scope),
    selections: getInitialAnswersForAllModules(
      groupQuestions,
      scope,
      offer,
      groupId,
      criticality,
      isDXEnabled,
      isCfalEnabled
    )
  })

  function buildEquipmentCategory(group: OnlyDoorsAssessmentGroup) {
    const buildCategory = (
      groupQuestions: OfferingQuestion[],
      category: AssessmentCriticality
    ): FlexibleCriticalities => ({
      normal: category.normal ? buildRatings(groupQuestions, category.normal, group.groupId, 'normal') : null,
      critical: null
    })

    function doorCategory(criticalities: AssessmentCriticality) {
      return Doors.fromObject({ door: buildCategory(questions.door, criticalities) })
    }

    return doorCategory(group.criticalities)
  }

  const groups = R.map(
    (group: any) => ({
      salesToolId: group.groupId,
      name: group.name,
      groupKind: group.groupKind,
      equipment: buildEquipmentCategory(group)
    }),
    assessment.groups
  )

  const interactionSelections = getInitialAnswersForInteraction(
    interactionQuestions,
    assessment.interactionScope,
    offer
  )

  return new Flexible(new DoorGroups(groups), {
    scope: assessment.interactionScope,
    selections: interactionSelections
  })
}

// Creates a new Flexible offer without taking existing scopes into account
export function makeFlexibleOffering(
  questions: GroupOfferingQuestions,
  assessment: ValidForFlexible,
  offer: Offer,
  isDXEnabled: boolean,
  isCfalEnabled: boolean
): Flexible {
  const buildRatings = (
    groupQuestions: OfferingQuestion[],
    scope: FlexibleScope,
    groupId: EquipmentGroupStId,
    criticality: Criticality
  ): FlexibleRatingsAndSelections => ({
    ratings: moduleScopesForRating(scope),
    selections: getInitialAnswersForAllModules(
      groupQuestions,
      scope,
      offer,
      groupId,
      criticality,
      isDXEnabled,
      isCfalEnabled
    )
  })
  function buildEquipmentCategory(group: AssessmentGroup) {
    const buildCategory = (
      groupQuestions: OfferingQuestion[],
      category: AssessmentCriticality
    ): FlexibleCriticalities => ({
      normal: category.normal ? buildRatings(groupQuestions, category.normal, group.groupId, 'normal') : null,
      critical: category.critical ? buildRatings(groupQuestions, category.critical, group.groupId, 'critical') : null
    })

    function elevatorsEscalatorsApfs(criticalities: ElevatorAndEscalatorCriticality) {
      return ElevatorsEscalatorsApfs.fromObject({
        elevator: criticalities.elevator ? buildCategory(questions?.elevator, criticalities.elevator) : null,
        escalator: criticalities.escalator ? buildCategory(questions?.escalator, criticalities.escalator) : null,
        resiflow: criticalities.resiflow ? buildCategory(questions?.resiflow, criticalities.resiflow) : null
      })
    }

    return elevatorsEscalatorsApfs(group.criticalities)
  }

  const groups = R.map(
    (group: any) => ({
      salesToolId: group.groupId,
      name: group.name,
      groupKind: group.groupKind,
      equipment: buildEquipmentCategory(group)
    }),
    assessment.groups
  )
  if (assessment.interactionScope) {
    return new Flexible(new ElevatorEscalatorApfGroups(groups), {
      scope: assessment.interactionScope,
      selections: {}
    } as Interaction)
  }
  return new Flexible(new ElevatorEscalatorApfGroups(groups), null as ResiFlowInteraction)
}

function makeAddendumServiceOffering(
  questions: GroupOfferingQuestions,
  interactionQuestions: OfferingInteractionQuestion[],
  assessment: ValidForValueAddedServiceAddendum,
  offer: Offer,
  isDXEnabled: boolean,
  isCfalEnabled: boolean
): ValueAddedServicesAddendum {
  const groups = R.map(
    (group: any) => ({
      salesToolId: group.groupId,
      name: group.name,
      groupKind: group.groupKind,
      selections: getInitialAnswersForAllModules(
        questions.valueAddedServices,
        'value_added_services_addendum',
        offer,
        group.groupId,
        null,
        isDXEnabled,
        isCfalEnabled
      )
    }),
    assessment.groups
  )

  const interactionSelections = getInitialAnswersForInteraction(
    interactionQuestions,
    'value_added_services_addendum',
    offer
  )

  return new ValueAddedServicesAddendum(groups, interactionSelections)
}

// TODO: Remove as part of GSM Clean up
function makeGSMServiceOffering(
  questions: GroupOfferingQuestions,
  interactionQuestions: OfferingInteractionQuestion[],
  assessment: ValidForGSMCampaign,
  offer: Offer,
  isDXEnabled: boolean,
  isCfalEnabled: boolean
): GSMCampaign {
  const groupArray = Array.isArray(assessment.groups) ? assessment.groups : Object.values(assessment.groups)
  const groups = groupArray.reduce(
    (acc, group: any) => {
      acc[group.groupId] = {
        salesToolId: group.groupId,
        name: group.name,
        groupKind: group.groupKind,
        selections: getInitialAnswersForAllModules(
          questions.gsm_campaign,
          'gsm_campaign',
          offer,
          group.groupId,
          null,
          isDXEnabled,
          isCfalEnabled
        )
      }
      return acc
    },
    {} as Record<string, any>
  )

  const interactionSelections = getInitialAnswersForInteraction(interactionQuestions, 'gsm_campaign', offer)

  return new GSMCampaign(groups, interactionSelections)
}

function moduleScopesForRating(rating: FlexibleScope): FlexibleModuleScopes {
  return {
    rating,
    moduleScopes: {
      basics: rating,
      performance: rating,
      technical: rating,
      value_added_services: rating,
      extra_services: rating,
      resiflow: rating,
      gsm_campaign: rating, // TODO: Remove as part of GSM Clean up
      people_flow: rating
    }
  }
}
